import { normalizeMultilingualValue } from "@/utils/multilingual";

const RELATION_FIELDS = [
  "platforms",
  "licenses",
  "researchers",
  "publications",
  "projects",
  "research_areas",
  "organizations",
  "datasets"
];

const LEGACY_LINK_FIELDS = [
  ["website_link", "website", ""],
  ["access_link", "remote_access", ""],
  ["download_source_link", "source_download", ""],
  ["download_binaries_link", "binary_download", ""],
  ["documentation_link", "documentation", ""],
  ["gitlab_link", "repository", "GitLab"],
  ["github_link", "repository", "GitHub"]
];

export function createDefaultSoftware() {
  return {
    id: null,
    slug: "",
    name: "",
    short_description: normalizeMultilingualValue(""),
    description: normalizeMultilingualValue(""),
    status: "active",
    software_type: "other",
    current_version: "",
    latest_release_date: "",
    keywords: [""],
    programming_languages: [""],
    local_execution_available: false,
    remote_access_available: false,
    source_access: "unavailable",
    source_access_instructions: "",
    platforms: [],
    licenses: [],
    launched_date: "",
    responsible_person: null,
    researchers: [],
    organizations: [],
    publications: [],
    projects: [],
    research_areas: [],
    datasets: [],
    doi: "",
    preferred_citation: "",
    publish_in_platforms_and_services: false,
    image_squared: "",
    image_horizontal: "",
    links: []
  };
}

export function relationIds(items) {
  if (!Array.isArray(items)) {
    return [];
  }
  return Array.from(new Set(items.map(item => {
    if (item && typeof item === "object") {
      return item.id !== undefined && item.id !== null ? item.id : item.value;
    }
    return item;
  }).filter(value => value !== undefined && value !== null && value !== "")));
}

export function normalizeSoftware(payload = {}) {
  let sourceAccess = payload.source_access;
  if (!sourceAccess) {
    if (payload.source_code_available || payload.source_available) {
      sourceAccess = "public";
    } else if (payload.source_code_available_under_agreement) {
      sourceAccess = "agreement";
    } else {
      sourceAccess = "unavailable";
    }
  }

  let links = Array.isArray(payload.links)
    ? payload.links.map((link, order) => ({ ...link, order }))
    : [];
  if (!links.length) {
    links = LEGACY_LINK_FIELDS.reduce((result, [field, linkType, label]) => {
      if (payload[field]) {
        result.push({ link_type: linkType, label, url: payload[field] });
      }
      return result;
    }, []);
  }

  const normalized = {
    ...createDefaultSoftware(),
    ...payload,
    short_description: normalizeMultilingualValue(
      payload.short_description || payload.description_short || ""
    ),
    description: normalizeMultilingualValue(payload.description || ""),
    launched_date: payload.launched_date || "",
    latest_release_date: payload.latest_release_date || "",
    keywords: Array.isArray(payload.keywords) && payload.keywords.length
      ? payload.keywords
      : [""],
    programming_languages:
      Array.isArray(payload.programming_languages) && payload.programming_languages.length
        ? payload.programming_languages
        : [""],
    source_access: sourceAccess,
    responsible_person: payload.responsible_person || null,
    links,
    local_execution_available: Boolean(
      payload.local_execution_available !== undefined
        ? payload.local_execution_available
        : payload.public_availability
    ),
    remote_access_available: Boolean(payload.remote_access_available)
  };

  RELATION_FIELDS.forEach(field => {
    normalized[field] = relationIds(payload[field]);
  });
  return normalized;
}

export function serializeSoftware(software) {
  const payload = {
    name: software.name || "",
    short_description: normalizeMultilingualValue(software.short_description),
    description: normalizeMultilingualValue(software.description),
    status: software.status || "active",
    software_type: software.software_type || "other",
    current_version: software.current_version || "",
    latest_release_date: software.latest_release_date || null,
    keywords: (software.keywords || []).filter(value => value && value.trim()),
    programming_languages: (software.programming_languages || [])
      .filter(value => value && value.trim()),
    local_execution_available: Boolean(software.local_execution_available),
    remote_access_available: Boolean(software.remote_access_available),
    source_access: software.source_access || "unavailable",
    source_access_instructions: software.source_access_instructions || "",
    launched_date: software.launched_date || null,
    responsible_person: relationIds([software.responsible_person])[0] || null,
    doi: software.doi || "",
    preferred_citation: software.preferred_citation || "",
    publish_in_platforms_and_services: Boolean(
      software.publish_in_platforms_and_services
    ),
    links: (software.links || [])
      .filter(link => link && link.link_type && link.url)
      .map((link, order) => ({
        link_type: link.link_type,
        label: link.label || "",
        url: link.url,
        order
      }))
  };
  RELATION_FIELDS.forEach(field => {
    payload[field] = relationIds(software[field]);
  });
  return payload;
}
